import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeDataToken, ExtendedRoutes } from 'models/routes';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { APP_ROUTES } from 'src/app/app.routes';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatBadge } from '@angular/material/badge';
import { AddNewDialogComponent } from './add-new-dialog/add-new-dialog.component';
import { defaultDialogConfig } from 'shared/utils/dialog-config';
import { UtilityService } from 'services/utility.service';
import { AccountService, UserRole } from 'services/account.service';
import { Company } from 'models/company';
import { DarkModeDirective } from 'directives/dark-mode.directive';
import { AppConfigService } from 'services/app-config.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'core-ui-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [
    CommonModule,
    MatSidenavModule,
    MatIconModule,
    MatDividerModule,
    RouterModule,
    TranslateModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatBadge,
    DarkModeDirective
  ]
})
export class NavbarComponent implements OnInit {
  public sidenavOpened = window.innerWidth >= 768;
  public currentRoute: string;
  public mobileView = false;
  public routeBadgeData = new Map<BadgeDataToken, number>();
  public selectedCompany: Company;
  public userRole: UserRole;
  public routes: ExtendedRoutes = [];
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private utilityService = inject(UtilityService);
  private accountService = inject(AccountService);
  private appConfigService: AppConfigService = inject(AppConfigService);
  private readonly defaultRoutes: ExtendedRoutes = [];

  constructor() {
    this.defaultRoutes = APP_ROUTES.filter((route) => !route.hidden);
    this.routes = this.defaultRoutes;
  }

  public ngOnInit(): void {
    this.userRole = this.accountService.userRole;

    this.initMobileViewListener();
    this.initSelectedCompanyListener();
    this.initRouterListener();
  }

  private initMobileViewListener(): void {
    this.utilityService
      .isMobileView$()
      .pipe(tap((isMobile) => (this.mobileView = isMobile)))
      .subscribe();
  }

  private initSelectedCompanyListener(): void {
    this.accountService
      .getSelectedCompany$()
      .pipe(switchMap((company) => this.handleSelectedCompanyChange$(company)))
      .subscribe();
  }

  private initRouterListener(): void {
    this.router.events
      .pipe(
        map((event: any) => event.urlAfterRedirects?.replace('/', '')),
        filter((route: string) => !!this.routes.find((r) => r.path === route)),
        tap((current) => (this.currentRoute = current))
      )
      .subscribe();
  }

  private handleSelectedCompanyChange$(company: Company): Observable<unknown> {
    return this.appConfigService.childFeaturesEnabled$().pipe(
      tap((childFeaturesEnabled) => {
        this.selectedCompany = company;
        if (this.userRole === UserRole.MSP) {
          this.routes = this.selectedCompany
            ? this.defaultRoutes.filter((r) =>
                this.appConfigService.routeEnabled(r.path, childFeaturesEnabled)
              )
            : this.defaultRoutes.filter((r) => r.path === 'partner-portal');
        } else {
          this.routes = this.defaultRoutes.filter((r) => {
            return (
              r.path !== 'partner-portal' &&
              this.appConfigService.routeEnabled(r.path, childFeaturesEnabled)
            );
          });
        }
      })
    );
  }

  public logout(): void {
    this.accountService.logout();
  }

  public addNew(): void {
    this.dialog.open(AddNewDialogComponent, defaultDialogConfig);
  }

  public get UserRole(): typeof UserRole {
    return UserRole;
  }
}
